/**
 * PreviewSlide — composed layer stack for a single slide.
 *
 * Compose order (back → front):
 *   1. SlideBackgroundLayer  (per-slide particles + radial wash)
 *   2. SlideContentLayer     (variant renderer wrapped in entry-effect)
 *   3. CinematicOverlaysLayer (vignette + light-leak + film grain)
 *
 * Removal note:
 *   The legacy `.preview-iris-cover` / `.preview-iris-reveal` pulse has
 *   been removed. The iris-cut animation is now driven exclusively by
 *   `transitions/PageTransitionStage` via `[data-t="iris"]` in
 *   `transitions/page-transitions.css` — either as the built-in default
 *   (no catalogue row picked) or when the admin explicitly selects it
 *   in the transition picker.
 *
 * Content updates while the preview is OPEN do not reset playback
 * state. The slide remounts (because the parent stage passes a fresh
 * `playbackKey`) so the entry animation replays, but the player's
 * index / direction / previous-slide snapshot are owned upstream.
 */
import { SlideBackgroundLayer } from './layers/SlideBackgroundLayer';
import { SlideContentLayer } from './layers/SlideContentLayer';
import { CinematicOverlaysLayer } from './layers/CinematicOverlaysLayer';
import './effects.css';

export interface PreviewSlideData {
  type: string;
  contentText: string;
  imageUrl: string;
  /** Legacy entry-effect code; kept for backward-compat but no longer drives
   *  animation. The runtime entry-effect wrapper still honours it via
   *  `slides/entry/entryRegistry`. */
  effectEntry?: string;
  effectAmbient: string;
  effectIntensity: number;
  /** Auto-advance duration in seconds. */
  duration?: number;
  /** When false, the slide waits for manual advance. */
  enableAutoAdvance?: boolean;
  /** Per-slide page transition override. `null` = use deck-level default. */
  pageTransitionId?: string | null;
}

interface PreviewSlideProps {
  slide: PreviewSlideData;
  /** Key change — when this changes, we remount the slide and re-run the
   *  entry animation. Pass the slide's `order` or a unique counter. */
  playbackKey: number | string;
  /** Whether to fire a light leak on this slide change. */
  lightLeakGold?: boolean;
}

export function PreviewSlide({ slide, playbackKey, lightLeakGold }: PreviewSlideProps) {
  return (
    <div key={playbackKey} className="preview-slide">
      <SlideBackgroundLayer slide={slide} />
      <SlideContentLayer slide={slide} />
      <CinematicOverlaysLayer
        slide={slide}
        playbackKey={playbackKey}
        lightLeakGold={lightLeakGold}
      />
    </div>
  );
}
